import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { TRButton } from "@/components/TRButton";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const { clear } = useCart();
    const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
    const attempts = useRef(0);
    const cleared = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let timer;
        const poll = async () => {
            if (attempts.current >= MAX_ATTEMPTS) {
                setStatus("timeout");
                return;
            }
            attempts.current += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    if (!cleared.current) {
                        clear();
                        cleared.current = true;
                    }
                    return;
                }
                if (data.payment_status === "failed") {
                    setStatus("error");
                    return;
                }
            } catch {
                setStatus("error");
                return;
            }
            timer = setTimeout(poll, 1600);
        };
        poll();
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    return (
        <div data-testid="payment-success-page" className="pt-40 md:pt-52 min-h-[70vh]">
            <div className="max-w-[560px] mx-auto px-5 sm:px-8 text-center">
                {status === "checking" && (
                    <>
                        <Loader2 className="mx-auto animate-spin text-ink/50" size={32} />
                        <h1 className="mt-8 font-serif text-4xl tracking-[-0.03em]">
                            Confirming your order…
                        </h1>
                        <p className="mt-4 text-ink/60">One moment please.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <div className="mx-auto w-16 h-16 border border-ink flex items-center justify-center">
                            <Check size={28} strokeWidth={1.5} />
                        </div>
                        <h1 className="mt-8 font-serif font-light text-5xl tracking-[-0.04em]" data-testid="payment-confirmed">
                            Thank you.
                        </h1>
                        <p className="mt-5 text-ink/70 leading-relaxed">
                            Your box is on its way to being made. Twenty premium
                            pairs, one less thing to think about.
                        </p>
                        <div className="mt-10 flex justify-center gap-4">
                            <TRButton as="link" to="/account">View Orders</TRButton>
                            <TRButton as="link" to="/" variant="outline">Home</TRButton>
                        </div>
                    </>
                )}
                {(status === "timeout" || status === "error") && (
                    <>
                        <h1 className="font-serif text-4xl tracking-[-0.03em]">
                            {status === "timeout" ? "Still processing" : "Something went wrong"}
                        </h1>
                        <p className="mt-4 text-ink/60">
                            {status === "timeout"
                                ? "Your payment is taking a little longer. Check your account shortly."
                                : "We couldn't confirm this payment. If you were charged, contact us."}
                        </p>
                        <div className="mt-10 flex justify-center gap-4">
                            <TRButton as="link" to="/account">Go to Account</TRButton>
                            <TRButton as="link" to="/cart" variant="outline">Back to Cart</TRButton>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
